import React, { useRef } from 'react';
import screenfull from 'screenfull';
import { useNavigate } from 'react-router-dom';
import { TvIcon } from 'lucide-react';
import { Translate } from '@utils/i18n';
import { Logger } from '_clean-code/utils/logger';
import { cn } from '@utils/tailwind-class.merge';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  dataTestId?: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
}

const ThisButton = ({
  children,
  onClick,
  ...rest
}: React.PropsWithChildren<ButtonProps>) => (
  <button
    tabIndex={0}
    className={cn(
      'tw-shadow tw-box-border tw-inline-flex tw-items-center tw-justify-center',
      'tw-whitespace-nowrap tw-gap-8px tw-rounded-md',
      'disabled:tw-cursor-not-allowed focus-visible:tw-outline-none tw-border tw-border-gray-100',
      '!tw-text-text-body tw-bg-gray-0 focus-visible:tw-shadow-outline-v2-focus',
      'disabled:tw-bg-gray-0 hover:tw-bg-gray-5 disabled:tw-text-text-disabled !tw-px-10px !tw-py-8px tw-text-sm'
    )}
    data-testid="tv-mode-button"
    onClick={onClick}
    {...rest}
  >
    {children}
  </button>
);
const TvModeButtonContent = () => {
  const navigate = useNavigate();
  const lastActivateAtRef = useRef(0);

  const isActivationEvent = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    const key = event.key;
    const keyCode = event.keyCode;
    return (
      key === 'Enter' ||
      key === ' ' ||
      key === 'Spacebar' ||
      keyCode === 13 ||
      keyCode === 32
    );
  };

  const activateOnce = async () => {
    const now = Date.now();
    if (now - lastActivateAtRef.current < 250) return;
    lastActivateAtRef.current = now;
    await handleActivate();
  };

  async function handleActivate() {
    navigate('/realtime/tv-mode-redirect');
    if ('wakeLock' in navigator) {
      try {
        // Using temporary global for wake lock transfer
        window.__preAcquiredWakeLock = await navigator.wakeLock.request(
          'screen'
        );
        Logger.info('Wake Lock pre-acquired on button click');
      } catch (err) {
        Logger.warn('Failed to pre-acquire Wake Lock:', err);
      }
    }

    if (screenfull.isEnabled) {
      try {
        await screenfull.request();
      } catch (error) {
        Logger.error('Failed to enter fullscreen:', error);
      }
    }
  }
  return (
    <ThisButton
      data-testid="tv-mode-button"
      onKeyDown={(event) => {
        if (isActivationEvent(event)) {
          event.preventDefault();
          activateOnce();
        }
      }}
      onKeyUp={(event) => {
        if (isActivationEvent(event)) {
          event.preventDefault();
          activateOnce();
        }
      }}
      onPointerUp={() => {
        activateOnce();
      }}
      onClick={() => {
        activateOnce();
      }}
    >
      <TvIcon className="tw-w-4 tw-h-4" />
      <Translate value="tv_version" />
    </ThisButton>
  );
};

export const TvModeButton = TvModeButtonContent;
